import React, {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';

import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import Link from '@material-ui/core/Link';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useSelector } from 'react-redux';
import TextInput from "../Fields/TextInput";
import {useTranslation} from "react-i18next";

const FILE_EXTENSIONS = {
	EXCEL: '.xlsx',
	CSV: '.csv',
	TSV: '.tsv',
	Word: '.doc',
	'Word Simple': '.doc',
	HTML: '.html',
};

const MAX_FILE_NAME_LENGTH = 100;

const useStyles = makeStyles((theme) => ({
	modalClassConDSI: {
		position: 'absolute',
		width: '105%',
		padding: '35px 15px 0px',
		right: 'auto',
		bottom: 'auto',
	},
	modalClassConDSIDark: {
		position: 'absolute',
		width: '105%',
		padding: '35px 15px 0px',
		right: 'auto',
		bottom: 'auto',
		backgroundColor: '#292929'
	},
	modalBoxContent: {
		maxHeight: '600px',
		backgroundColor: '#292929'
	},
	modalText: {
		position: 'absolute',
		top: '10px',
		right: '10px'
	},
}));

const DiscoverExportAllModal = props => {
	const { t } = useTranslation("common");
	const classes = useStyles();
	const [showOptions, setShowOptions] = useState(false);
	const [fileName, setFileName] = useState('');
	const [fileNameError, setFileNameError] = useState(false);
	const fileNameRef = React.useRef(null);
	const fileExtension = FILE_EXTENSIONS[props.downloadType] || '';
	const [auditInfo, setAuditInfo] = useState(true);
	const [auditOnBottom, setAuditOnBottom] = useState(false);
	const [overview, setOverview] = useState(true);
	const [includeResult, setIncludeResult] = useState(props.includeResult || true);
	const [includeAli, setIncludeAli] = useState(true);
	const [clipAli, setClipAli] = useState(true);
	const [includeAnnotation, setIncludeAnnotation] = useState(props.includeAnnotation || false);
	const [excludeClaim, setExcludeClaim] = useState(false);
	const [excludeFamilyData, setExcludeFamilyData] = useState(false);
	const [includeSS, setIncludeSS] = useState(props.includeSS === undefined ? true : props.includeSS);
	const [includeQS, setIncludeQS] = useState(true);
	const [includeFullSeq, setIncludeFullSeq] = useState(true);
	const toggle = useSelector(state => state.setToggle.toggle);

	useEffect(() => {
		if (props.includeAnnotation !== undefined) {
			setIncludeAnnotation(props.includeAnnotation);
		}
		if (props.includeSS !== undefined) {
			setIncludeSS(props.includeSS);
		}
		if (props.includeQS !== undefined) {
			setIncludeQS(props.includeQS);
		}
		if (props.includeFullSeq !== undefined) {
			setIncludeFullSeq(props.includeFullSeq);
		}
	}, [props]);

	// The Modal stays mounted and is only toggled via `show`, so the file name needs to be
	// re-seeded from the result set's title every time the modal opens.
	useEffect(() => {
		if (props.showModal) {
			setFileName((props.resInfo?.title || '').slice(0, MAX_FILE_NAME_LENGTH));
			setFileNameError(false);
		}
	}, [props.showModal, props.resInfo, props.downloadType]);

	const changeFileName = (e) => {
		// inputProps.maxLength below already stops most typing/pasting past the limit,
		// but doesn't cover every path (e.g. IME composition), so enforce it here too.
		const val = e.target.value.slice(0, MAX_FILE_NAME_LENGTH);
		setFileName(val);
		setFileNameError(!val || val.trim().length === 0);
	}

	const callback = () => {
		if (!fileName || fileName.trim().length === 0) {
			setFileNameError(true);
			return;
		}
		let options = {};
		options.auditInfo = auditInfo;
		options.auditOnTop = !auditOnBottom;
		options.overview = overview;
		options.includeResult = includeResult;
		options.includeAli = includeResult && includeAli;
		options.clipAli = includeResult && clipAli;
		options.excludeClaim = includeResult && excludeClaim;
		options.excludeFamilyData = includeResult && excludeFamilyData;
		options.includeAnnotation = includeResult && includeAnnotation;
		options.includeSS = includeSS;
		options.includeQS = !props.isCmNoSeq && includeQS;
		options.includeFullSeq = includeFullSeq;
		props.resInfo.options = options;
		props.actionModal(props.fieldConfig, props.filter, props.resInfo, props.downloadType,
			undefined, undefined, undefined, undefined, fileName.trim());
	}

	return (
		<Modal
			size="lg"
			aria-labelledby="contained-modal-title-vcente"
			centered
			show={props.showModal}
			contentClassName={toggle? classes.modalClassConDSI:classes.modalClassConDSIDark}
			onEntered={() => fileNameRef.current?.focus()}
		>
			<Link href="#" onClick={(e) => e.preventDefault()} className={classes.modalText} >
				<CloseIcon style = {{ fill : '#777777'}} onClick={props.closeModal} />
			</Link>
			<br></br>
			<div className={toggle? "jumbotron appTextColor": "jumbotronDark appTextColor" } style={toggle ? {backgroundColor: '#eee',padding: '10px', marginBottom: '20px'}:{backgroundColor: 'rgb(238 238 238 / 12%)', padding: '10px', marginBottom: '20px'}}>
				<br></br>
				<br></br>
				<h1 style={{paddingTop: '0px', fontSize: '63px'}}>Export to {props.downloadTypeDesc}</h1>
				<p className="mb-3" style={{fontSize: '21px',fontFamily: 'Helvetica', fontWeight: '200', wordSpacing: '-2px' }}>
					Your {props.downloadTypeDesc} file is generated in the background.
					You can continue to work on your results.
					As soon as your download is ready, we will let you know.
				</p>
				{props.alertMessage && props.alertMessage !== '' &&
					<div><b style={{color: 'red'}}>
						{props.alertMessage}
					</b></div>
				}
				{props.downloadType === 'EXCEL' && props.totalAlignmentNumber > 2000 &&
					<div><b style={{color: 'red'}}>
						Alignments are uncolored in exports with more than 2,000 records.
					</b></div>
				}
				{props.downloadType === 'EXCEL' &&
					<div>
						<a href="#" onClick={(e) => {setShowOptions(!showOptions); e.preventDefault();}}>Advanced Options</a>
						{showOptions &&
							<div>
								<div>
									<input type="checkbox" style={{verticalAlign: 'middle'}}
											checked={includeAnnotation} id="includeAnnotation"
											onChange={() => setIncludeAnnotation(!includeAnnotation)}/>
									<label htmlFor="includeAnnotation">&nbsp;Include annotation fields</label>
								</div>
								<div>
									<input type="checkbox" style={{verticalAlign: 'middle'}}
										   checked={includeSS} id="includeSS"
										   onChange={() => setIncludeSS(!includeSS)}/>
									<label htmlFor="includeSS">&nbsp;Subject Sequences</label>
								</div>
								<div>
									<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={props.isCmNoSeq}
										   checked={!props.isCmNoSeq && includeQS} id="includeQS" onChange={() => setIncludeQS(!includeQS)}/>
									<label htmlFor="includeQS">&nbsp;Query Sequences</label>
								</div>
								{(() => {
									const hasGqAlignmentCol = Array.isArray(props.displayedColumns) &&
										(props.displayedColumns.includes('RESULT_FALI_FORMATTED') || props.displayedColumns.includes('RESULT_EXTENDED_ALI'));
									const fullSeqDisabled = !hasGqAlignmentCol;
									return (
										<div>
											<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={fullSeqDisabled}
												checked={!fullSeqDisabled && includeFullSeq} id="includeFullSeq" onChange={() => setIncludeFullSeq(!includeFullSeq)}/>
											<label htmlFor="includeFullSeq">&nbsp;Show alignment with the full query sequence</label>
										</div>
									);
								})()}
							</div>
						}
					</div>
				}
				{(props.downloadType === 'Word' || props.downloadType === 'HTML') &&
					<div>
						<a href="#" onClick={(e) => {
							setShowOptions(!showOptions);
							e.preventDefault();
						}}>Advanced Options</a>
						{showOptions &&
							<div>
							<input type="checkbox"  style={{verticalAlign: 'middle'}}
									   checked={auditInfo} id="auditInfo" onChange={() => setAuditInfo(!auditInfo)}/>
								<label htmlFor="auditInfo">&nbsp;Launch Info</label>
								<div style={{paddingLeft: '15px'}}>
									<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={!auditInfo}
										   checked={auditOnBottom} id="auditOnBottom" onChange={() => setAuditOnBottom(!auditOnBottom)}/>
									<label htmlFor="auditOnBottom">&nbsp;Put launch info at the end</label>
								</div>
								<div>
									<input type="checkbox"  style={{verticalAlign: 'middle'}}
										   checked={overview} id="overview" onChange={() => setOverview(!overview)}/>
									<label htmlFor="overview">&nbsp;Overview</label>
								</div>
								<div>
									<input type="checkbox"  style={{verticalAlign: 'middle'}}
										   checked={includeResult} id="includeResult" onChange={() => setIncludeResult(!includeResult)}/>
									<label htmlFor="includeResult">&nbsp;Results</label>
									<div style={{paddingLeft: '15px'}}>
										<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={!includeResult}
											   checked={includeAli} id="includeAli" onChange={() => setIncludeAli(!includeAli)}/>
										<label htmlFor="includeAli">&nbsp;Include alignments</label>
									</div>
									<div style={{paddingLeft: '15px'}}>
										<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={!includeResult}
											   checked={clipAli} id="clipAli" onChange={() => setClipAli(!clipAli)}/>
										<label htmlFor="clipAli">&nbsp;Clip alignments after 15 lines</label>
									</div>
									<div style={{paddingLeft: '15px'}}>
										<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={!includeResult}
											   checked={includeAnnotation} id="includeAnnotation" onChange={() => setIncludeAnnotation(!includeAnnotation)}/>
										<label htmlFor="includeAnnotation">&nbsp;Include annotation fields</label>
									</div>
									<div style={{paddingLeft: '15px'}}>
										<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={!includeResult}
											   checked={excludeClaim} id="excludeClaim" onChange={() => setExcludeClaim(!excludeClaim)}/>
										<label htmlFor="excludeClaim">&nbsp;Exclude claim field</label>
									</div>
									<div style={{paddingLeft: '15px'}}>
										<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={!includeResult}
											   checked={excludeFamilyData} id="excludeFamilyData" onChange={() => setExcludeFamilyData(!excludeFamilyData)}/>
										<label htmlFor="excludeFamilyData">&nbsp;Exclude family member fields</label>
									</div>
								</div>
								<div>
									<input type="checkbox"  style={{verticalAlign: 'middle'}}
										   checked={includeSS} id="includeSS" onChange={() => setIncludeSS(!includeSS)}/>
									<label htmlFor="includeSS">&nbsp;Subject Sequences</label>
								</div>
								<div>
									<input type="checkbox"  style={{verticalAlign: 'middle'}} disabled={props.isCmNoSeq}
										   checked={!props.isCmNoSeq && includeQS} id="includeQS" onChange={() => setIncludeQS(!includeQS)}/>
									<label htmlFor="includeQS">&nbsp;Query Sequences</label>
								</div>
							</div>
						}
					</div>
				}
				<br></br>
				<div className="filter-widget-textarea" style={{ border: 'none' }}>
					<label style={{padding: '0px'}}>File name:</label>
					<TextInput id="exportFileName" name="exportFileName" variant="outlined" inputRef={fileNameRef} onChange={changeFileName}
							   value={fileName} inputProps={{maxLength: MAX_FILE_NAME_LENGTH}} error={fileNameError} fullWidth={true}
							   spellcheck="false" helperText={fileNameError ? t('required') : ""}
							   InputProps={{endAdornment: <InputAdornment position="end">{fileExtension}</InputAdornment>}}/>
				</div>
				<br></br>
				<div style={{paddingTop: '10px', paddingBottom: '0px', margin : '12px', marginBottom: '70px'}}>
					<button onClick={callback} className="pull-right large-button gqblue">Start Export</button>
					<button onClick={props.closeModal} className="pull-right large-button grey">Cancel</button>
				</div>
			</div>
		</Modal>
	);
}

export default DiscoverExportAllModal;
